import { Link } from "react-router-dom";
import "./WorkTimeDetails.css";
import BackButton from "../../../../../sharedComponents/backButton/BackButton";
import { useEffect, useState } from "react";
import axios from "axios";
import LoadingSpinner from "../../../../../sharedComponents/LoadingSpinner/LoadingSpinner";
import { DataGrid } from '@mui/x-data-grid';
import PrimaryButton from "../../../../../sharedComponents/primaryButton/PrimaryButton";
import extractTimeFromDateTime from "../../../../../sharedComponents/ExtractTimeFromDateTime";
import { getISOWeek, getYear } from 'date-fns';
import EmployeeWorkTimeDetails from "./employeeDetails/EmployeeWorkTimeDetails";
import FilterArea from "../../../../../sharedComponents/filterArea/FilterArea";

const columns = [
    { field: 'firstName', headerName: 'Vorname', width: 150 },
    { field: 'lastName', headerName: 'Nachname', width: 150 },
    { field: 'date', headerName: 'Datum', width: 150 },
    { field: 'startTime', headerName: "Startzeit", width: 150 },
    { field: 'endTime', headerName: "Endzeit", width: 150 },
    { field: 'location', headerName: "Ort", width: 150 },
    { field: 'workTime', headerName: "Arbeitszeit", width: 150 },
    { field: 'pauseTime', headerName: "Pausenzeit", width: 150 },
];

function formatWeekRange(startDate) {
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return `${startDate.toLocaleDateString("de-DE", options)} - ${endDate.toLocaleDateString("de-DE", options)}`;
}

function getMondayOfCurrentWeek(date) {
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Sunday (0) should become the last day of the previous week
    const monday = new Date(date);
    monday.setDate(date.getDate() + diff);
    return monday;
}

function WorkTimeDetails(props) {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [shifts, setShifts] = useState([]);
    const [currentDate, setCurrentDate] = useState(getMondayOfCurrentWeek(new Date()));

    const [selectedArea, setSelectedArea] = useState(null);

    useEffect(() => {
        fetchWorkTime(currentDate);
    }, [currentDate]);

    const fetchWorkTime = (date) => {
        setIsLoading(true);
        setHasError(false);
        const year = getYear(date);
        const isoCalendarWeek = getISOWeek(date);

        axios.get(`shift/getAllByIsoCalendarWeek`, {
            params: { year, isoCalendarWeek }
        }).then(results => {
            setShifts(results.data || []);
            setIsLoading(false);
        }).catch(() => {
            setHasError(true);
            setIsLoading(false);
        });
    };

    const generateRows = () => {
        let rows = [];

        shifts.forEach(shift => {
            const areaMatches = selectedArea === null || selectedArea.assigned_employee_ids.includes(shift.employee.id);

            if (shift.duration_working && shift.duration_in_pause && areaMatches) {
                const location = shift.location ? shift.location : "Nicht angegeben";
                const employee = shift.employee ? shift.employee : "Nicht angegeben";
                let row = {
                    id: shift.shift_begin_activity_id,
                    firstName: employee.first_name,
                    lastName: employee.last_name,
                    date: new Date(shift.start_time).toLocaleDateString("de-DE", { day: '2-digit', month: '2-digit', year: 'numeric' }),
                    startTime: extractTimeFromDateTime(shift.start_time),
                    endTime: extractTimeFromDateTime(shift.end_time),
                    location: location.name,
                    workTime: shift.duration_working.hours + " Std. " + shift.duration_working.minutes + " Min.",
                    pauseTime: shift.duration_in_pause.hours + " Std. " + shift.duration_in_pause.minutes + " Min."
                };
                rows.push(row);
            }
        });

        return rows;
    };

    const handlePreviousWeek = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() - 7);
            return newDate;
        });
    };

    const handleNextWeek = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + 7);
            return newDate;
        });
    };

    const generateDataTable = () => {
        return isLoading ?
            <LoadingSpinner />
            :
            hasError ?
            <p>Ein Fehler ist aufgetreten</p>
            :
            <DataGrid
                rows={generateRows()}
                columns={columns}
                disableRowSelectionOnClick
                hideFooterPagination
                density="compact"
                disableColumnMenu
                hideFooter
                className="work-time-data-grid"
            />
    };

    const generateSumOfWorkTime = () => {
        let totalWorkMinutes = 0;
        let totalPauseMinutes = 0;

        shifts.forEach(shift => {
            if (shift.duration_working && shift.duration_in_pause) {
                totalWorkMinutes += shift.duration_working.hours * 60 + shift.duration_working.minutes;
                totalPauseMinutes += shift.duration_in_pause.hours * 60 + shift.duration_in_pause.minutes;
            }
        });

        const totalWorkHours = Math.floor(totalWorkMinutes / 60);
        const remainingWorkMinutes = totalWorkMinutes % 60;

        const totalPauseHours = Math.floor(totalPauseMinutes / 60);
        const remainingPauseMinutes = totalPauseMinutes % 60;

        return (
            <div className="work-time-summary">
                <span>Gesamtarbeitszeit: </span>
                <span className="bold">
                    {totalWorkHours.toString().padStart(2, "0")} Std. {remainingWorkMinutes.toString().padStart(2, "0")} Min.
                </span>
                <span>Gesamtpausenzeit: </span>
                <span className="bold">
                    {totalPauseHours.toString().padStart(2, "0")} Std. {remainingPauseMinutes.toString().padStart(2, "0")} Min.
                </span>

                <EmployeeWorkTimeDetails shifts={shifts} />
            </div>
        )
    };

    return (
        <div className="work-time-details-container">
            <Link to={'/home/dashboard'}>
                <div className="work-time-header-container">
                    <BackButton text="Zurück" />
                </div>
            </Link>
            <div className="work-time-dashboard">
                <div className="work-time-dashboard-header">
                    <h3>Arbeitszeiten</h3>
                    <PrimaryButton text="Drucken" onClick={() => window.print()} />
                    <FilterArea selectedArea={selectedArea} setSelectedArea={setSelectedArea} />
                </div>
                <div className="week-navigation">
                    <img src={process.env.PUBLIC_URL + '/icons/forward.png'} onClick={handlePreviousWeek} alt="Rückwärts Icon" className='backward-icon' />
                    <span>{formatWeekRange(currentDate)}</span>
                    <img src={process.env.PUBLIC_URL + '/icons/forward.png'} onClick={handleNextWeek} alt="Vorwärts Icon" className='forward-icon' />
                    <PrimaryButton text="Heute" onClick={() => setCurrentDate(getMondayOfCurrentWeek(new Date()))} />
                </div>                
                {generateDataTable()}
                {generateSumOfWorkTime()}
            </div>
        </div>
    );
}

export default WorkTimeDetails;